import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useContact,
  useUpdateContact,
  useContactComments,
  useAddComment,
  useDeleteComment,
  useTagSuggestions,
  useReplaceContactTags,
  useSyncReporter,
  type SyncDiffEntry,
} from '../hooks/useContacts';
import { useReporter } from '../hooks/useReporters';
import { useMessages } from '../hooks/useSendBatches';
import {
  usePressLists,
  useAddContactToList,
  useRemoveContactFromList,
} from '../hooks/usePressLists';
import ThreadViewModal from '../components/ThreadViewModal';

// ── Initials avatar ────────────────────────────────────────────────────────────

function InitialsAvatar({
  firstName,
  lastName,
  imageUrl,
  size = 'md',
}: {
  firstName: string;
  lastName: string;
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
  };
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={`${firstName} ${lastName}`}
        className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0`}
      />
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-full bg-blue-100 text-blue-700 font-semibold flex items-center justify-center flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

// ── Inline editable field ──────────────────────────────────────────────────────

function EditableField({
  label,
  value,
  fieldKey,
  type = 'text',
  multiline = false,
  onSave,
}: {
  label: string;
  value: string | null | undefined;
  fieldKey: string;
  type?: string;
  multiline?: boolean;
  onSave: (key: string, value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function startEdit() {
    setDraft(value ?? '');
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    onSave(fieldKey, draft);
  }

  function cancel() {
    setEditing(false);
    setDraft(value ?? '');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      cancel();
    }
  }

  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </dt>
      {editing ? (
        multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            className="w-full border border-blue-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            className="w-full border border-blue-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )
      ) : (
        <dd
          onClick={startEdit}
          className="text-sm text-gray-900 cursor-text hover:bg-gray-50 rounded px-1 -mx-1 py-0.5 min-h-[1.5rem] group relative"
          title="Click to edit"
        >
          {value || (
            <span className="text-gray-400 italic">Click to add {label.toLowerCase()}</span>
          )}
          <span className="absolute right-1 top-0.5 opacity-0 group-hover:opacity-100 text-gray-400 transition-opacity">
            <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </span>
        </dd>
      )}
    </div>
  );
}

// ── Tag editor ─────────────────────────────────────────────────────────────────

function TagEditor({
  contactId,
  tags,
}: {
  contactId: string;
  tags: string[];
}) {
  const { data: suggestionsData } = useTagSuggestions();
  const replaceTags = useReplaceContactTags();
  const [input, setInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const allSuggestions = suggestionsData?.tags.map((t) => t.tag) ?? [];
  const filteredSuggestions = input.trim()
    ? allSuggestions.filter(
        (t) => t.includes(input.toLowerCase()) && !tags.includes(t),
      )
    : allSuggestions.filter((t) => !tags.includes(t));

  function addTag(tag: string) {
    const normalized = tag.toLowerCase().trim();
    if (!normalized || tags.includes(normalized)) {
      setInput('');
      setShowDropdown(false);
      return;
    }
    replaceTags.mutate({ id: contactId, tags: [...tags, normalized] });
    setInput('');
    setShowDropdown(false);
  }

  function removeTag(tag: string) {
    replaceTags.mutate({ id: contactId, tags: tags.filter((t) => t !== tag) });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) addTag(input);
    }
    if (e.key === 'Escape') {
      setInput('');
      setShowDropdown(false);
    }
  }

  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
        Tags
      </dt>
      <div className="flex flex-wrap gap-1 items-center">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="text-gray-400 hover:text-gray-600 ml-0.5 leading-none"
              aria-label={`Remove tag ${tag}`}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </span>
        ))}
        <div className="relative">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShowDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            placeholder={tags.length === 0 ? 'Add tags...' : '+'}
            className="text-xs border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 w-24 min-w-0"
          />
          {showDropdown && filteredSuggestions.length > 0 && (
            <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto min-w-[140px]">
              {filteredSuggestions.slice(0, 10).map((suggestion) => (
                <button
                  key={suggestion}
                  onMouseDown={() => addTag(suggestion)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Comments section ───────────────────────────────────────────────────────────

function CommentsSection({ contactId }: { contactId: string }) {
  const { data, isLoading } = useContactComments(contactId);
  const addComment = useAddComment(contactId);
  const deleteComment = useDeleteComment(contactId);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const commentsList = data?.comments ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setSubmitting(true);
    try {
      await addComment.mutateAsync(draft.trim());
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Notes & Comments</h2>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading comments...</p>
      ) : commentsList.length === 0 ? (
        <p className="text-sm text-gray-400 mb-4">No comments yet.</p>
      ) : (
        <ul className="space-y-3 mb-4">
          {commentsList.map((c) => (
            <li key={c.id} className="flex gap-3 group">
              <div className="flex-1 bg-gray-50 rounded-md px-3 py-2">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{c.text}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(c.createdAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => deleteComment.mutate(c.id)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity self-start mt-1"
                title="Delete comment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note or comment..."
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={!draft.trim() || submitting}
          className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
        >
          {submitting ? 'Adding...' : 'Add'}
        </button>
      </form>
    </div>
  );
}

// ── Relative time helper ───────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 bg-green-50 text-green-800 border border-green-200 max-w-sm">
      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      {message}
    </div>
  );
}

// ── Contact message history ────────────────────────────────────────────────────

function ContactMessageStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    queued: 'bg-yellow-100 text-yellow-800',
    sending: 'bg-blue-100 text-blue-800',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    opened: 'bg-purple-100 text-purple-800',
    replied: 'bg-indigo-100 text-indigo-800',
    bounced: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function ContactMessageHistory({ contactId }: { contactId: string }) {
  const [timelineSearch, setTimelineSearch] = useState('');
  const debouncedTimelineSearch = useDebounce(timelineSearch, 300);
  const { data, isLoading } = useMessages({
    contactId,
    limit: 50,
    q: debouncedTimelineSearch || undefined,
  });
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const msgs = data?.messages ?? [];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Message History</h2>
        <input
          type="search"
          placeholder="Search pitches & replies..."
          value={timelineSearch}
          onChange={(e) => setTimelineSearch(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
        />
      </div>
      {isLoading && <p className="text-sm text-gray-400">Loading messages...</p>}
      {!isLoading && msgs.length === 0 && (
        <p className="text-sm text-gray-400">
          {debouncedTimelineSearch ? 'No messages match your search.' : 'No messages sent to this contact yet.'}
        </p>
      )}
      {!isLoading && msgs.length > 0 && (
        <div className="divide-y divide-gray-100">
          {msgs.map((msg) => (
            <div
              key={msg.id}
              onClick={() => setSelectedMessageId(msg.id)}
              className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 truncate">
                  {msg.subject ?? <span className="italic text-gray-400">no subject</span>}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {msg.sentAt
                    ? new Date(msg.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : new Date(msg.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  {msg.openCount > 0 && (
                    <span className="ml-2 text-purple-600">{msg.openCount} open{msg.openCount !== 1 ? 's' : ''}</span>
                  )}
                </p>
              </div>
              <div className="ml-3 shrink-0">
                <ContactMessageStatusBadge status={msg.status} />
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedMessageId && (
        <ThreadViewModal messageId={selectedMessageId} onClose={() => setSelectedMessageId(null)} />
      )}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useContact(id);
  const updateContact = useUpdateContact();
  const [toast, setToast] = useState<string | null>(null);
  const [showAddToList, setShowAddToList] = useState(false);
  const [removingListId, setRemovingListId] = useState<string | null>(null);

  const contact = data?.contact;

  const syncReporter = useSyncReporter(id ?? '');
  const { data: reporterData } = useReporter(contact?.reporterId ?? undefined);
  const fullReporter = reporterData?.data;
  const { data: pressListsData } = usePressLists();
  const addToList = useAddContactToList();
  const removeFromList = useRemoveContactFromList();

  async function handleFieldSave(key: string, value: string) {
    if (!id || !contact) return;
    const body: Record<string, string | undefined> = {
      [key]: value || undefined,
    };
    // firstName and lastName must not be blank
    if ((key === 'firstName' || key === 'lastName') && !value.trim()) return;
    await updateContact.mutateAsync({ id, body });
  }

  async function handleSync() {
    try {
      const result = await syncReporter.mutateAsync();
      const diff: SyncDiffEntry[] = result.diff;
      if (diff.length === 0) {
        setToast('Contact is already up to date with reporter data');
      } else {
        const fields = diff.map((d) => d.field).join(', ');
        setToast(`Updated from reporter: ${fields}`);
      }
    } catch {
      setToast('Failed to sync reporter data');
    }
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (isError || !contact) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Failed to load contact.</p>
        <button
          onClick={() => navigate('/contacts')}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Back to Contacts
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate('/contacts')}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Contacts
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-900 font-medium">
          {contact.firstName} {contact.lastName}
        </span>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start gap-4">
          <InitialsAvatar
            firstName={contact.firstName}
            lastName={contact.lastName}
            imageUrl={contact.imageUrl}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-900">
                {contact.firstName} {contact.lastName}
              </h1>
              {contact.reporterId && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                  Linked Reporter
                </span>
              )}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                {contact.source}
              </span>
              <Link
                to={`/compose?contactId=${contact.id}`}
                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Compose
              </Link>
            </div>
            {contact.title && (
              <p className="text-sm text-gray-500 mt-0.5">{contact.title}</p>
            )}
            {contact.company && (
              <p className="text-sm text-gray-500">{contact.company}</p>
            )}
          </div>
        </div>

        {/* Tags */}
        {id && (
          <div className="mt-4">
            <TagEditor contactId={id} tags={contact.tags ?? []} />
          </div>
        )}
      </div>

      {/* Contact info — inline editable */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Contact Info</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
          <EditableField
            label="First Name"
            fieldKey="firstName"
            value={contact.firstName}
            onSave={handleFieldSave}
          />
          <EditableField
            label="Last Name"
            fieldKey="lastName"
            value={contact.lastName}
            onSave={handleFieldSave}
          />
          <EditableField
            label="Email"
            fieldKey="email"
            value={contact.email}
            type="email"
            onSave={handleFieldSave}
          />
          <EditableField
            label="Phone"
            fieldKey="phone"
            value={contact.phone}
            type="tel"
            onSave={handleFieldSave}
          />
          <EditableField
            label="Title"
            fieldKey="title"
            value={contact.title}
            onSave={handleFieldSave}
          />
          <EditableField
            label="Company"
            fieldKey="company"
            value={contact.company}
            onSave={handleFieldSave}
          />
          <div className="col-span-2">
            <EditableField
              label="Notes"
              fieldKey="notes"
              value={contact.notes}
              multiline
              onSave={handleFieldSave}
            />
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {new Date(contact.createdAt).toLocaleDateString()}
            </dd>
          </div>
        </dl>
      </div>

      {/* Reporter intelligence */}
      {contact.reporter && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Section header with sync controls */}
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              Reporter Intelligence
            </h2>
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleSync}
                disabled={syncReporter.isPending}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <svg className={`w-3.5 h-3.5 ${syncReporter.isPending ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {syncReporter.isPending ? 'Refreshing...' : 'Refresh from Reporter'}
              </button>
              {contact.reporterSyncedAt && (
                <p className="text-xs text-gray-400">
                  Last synced {relativeTime(contact.reporterSyncedAt)}
                </p>
              )}
            </div>
          </div>

          {/* Reporter profile row */}
          <div className="flex items-start gap-4">
            <InitialsAvatar
              firstName={contact.reporter.firstName ?? contact.firstName}
              lastName={contact.reporter.lastName ?? contact.lastName}
              imageUrl={contact.reporter.imageUrl}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{contact.reporter.name}</p>
              {contact.reporter.title && (
                <p className="text-sm text-gray-500">{contact.reporter.title}</p>
              )}
              {contact.reporter.primaryPublication && (
                <Link
                  to={`/publications/${contact.reporter.primaryPublication.id}`}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {contact.reporter.primaryPublication.name}
                </Link>
              )}

              {/* Beats */}
              {contact.reporter.beats && contact.reporter.beats.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {contact.reporter.beats.map((beat) => (
                    <span
                      key={beat}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                    >
                      {beat}
                    </span>
                  ))}
                </div>
              )}

              {/* Bio */}
              {contact.reporter.bio && (
                <p className="mt-2 text-sm text-gray-600">{contact.reporter.bio}</p>
              )}

              {/* Social links */}
              <div className="mt-3 flex gap-4">
                {contact.reporter.twitter && (
                  <a
                    href={`https://twitter.com/${contact.reporter.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Twitter/X
                  </a>
                )}
                {contact.reporter.linkedin && (
                  <a
                    href={contact.reporter.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Full publication history */}
          {fullReporter && fullReporter.publications && fullReporter.publications.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Publication History</p>
              <ul className="space-y-1">
                {fullReporter.publications.map((pub) => (
                  <li key={pub.id} className="flex items-center gap-2 text-sm">
                    <Link
                      to={`/publications/${pub.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {pub.name}
                    </Link>
                    {pub.isPrimary && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Current</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* View full profile link */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => navigate(`/reporters/${contact.reporterId}`)}
              className="text-sm text-blue-600 hover:underline"
            >
              View full reporter profile →
            </button>
          </div>
        </div>
      )}

      {/* Press list memberships */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Press Lists</h2>
          <button
            onClick={() => setShowAddToList((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            + Add to list
          </button>
        </div>

        {showAddToList && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-xs text-gray-500 mb-2">Select a list to add this contact:</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {(pressListsData as unknown as { pressLists: { id: string; name: string }[] } | undefined)?.pressLists
                ?.filter((l) => !contact.pressListMemberships?.some((m) => m.id === l.id))
                .map((l) => (
                  <button
                    key={l.id}
                    onClick={async () => {
                      if (!id) return;
                      try {
                        await addToList.mutateAsync({ listId: l.id, body: { contactId: id } });
                        setToast(`Added to "${l.name}"`);
                        setShowAddToList(false);
                      } catch {
                        setToast('Failed to add to list');
                      }
                    }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-white rounded"
                  >
                    {l.name}
                  </button>
                ))}
              {(pressListsData as unknown as { pressLists: { id: string; name: string }[] } | undefined)?.pressLists
                ?.filter((l) => !contact.pressListMemberships?.some((m) => m.id === l.id)).length === 0 && (
                <p className="text-sm text-gray-400">Already on all lists.</p>
              )}
            </div>
          </div>
        )}

        {contact.pressListMemberships && contact.pressListMemberships.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {contact.pressListMemberships.map((list) => {
              const statusColors: Record<string, string> = {
                sent: 'bg-blue-100 text-blue-700',
                in_followup_window: 'bg-yellow-100 text-yellow-700',
                replied: 'bg-green-100 text-green-700',
                no_response: 'bg-gray-100 text-gray-600',
                bounced: 'bg-red-100 text-red-700',
                written: 'bg-purple-100 text-purple-700',
              };
              const statusLabels: Record<string, string> = {
                sent: 'Pitched',
                in_followup_window: 'Follow-up due',
                replied: 'Replied',
                no_response: 'No response',
                bounced: 'Bounced',
                written: 'Draft',
              };
              const statusKey = list.outreachStatus ?? '';
              return (
                <li key={list.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/press-lists/${list.id}`}
                      className="text-sm text-blue-600 hover:underline font-medium"
                    >
                      {list.name}
                    </Link>
                    {list.lastOutreachAt && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Last pitched {new Date(list.lastOutreachAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {list.outreachStatus && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[statusKey] ?? 'bg-gray-100 text-gray-600'}`}>
                        {statusLabels[statusKey] ?? list.outreachStatus}
                      </span>
                    )}
                    {list.locked ? (
                      <span title="Locked — message sent" className="text-gray-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : removingListId === list.id ? (
                      <span className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Remove?</span>
                        <button
                          onClick={async () => {
                            if (!id) return;
                            try {
                              await removeFromList.mutateAsync({ listId: list.id, contactId: id });
                              setToast(`Removed from "${list.name}"`);
                            } catch {
                              setToast('Failed to remove from list');
                            }
                            setRemovingListId(null);
                          }}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setRemovingListId(null)}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setRemovingListId(list.id)}
                        className="text-gray-300 hover:text-red-400 transition-colors"
                        title="Remove from list"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">Not on any press lists yet.</p>
        )}
      </div>

      {/* Message history */}
      {id && <ContactMessageHistory contactId={id} />}

      {/* Comments */}
      {id && <CommentsSection contactId={id} />}

      {/* Toast notification */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
